import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IImpact } from "@/pages/dashboard/utils/types";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface TravelMode {
  id: number;
  name: string;
  relations: number;
  urn?: string;
  unit?: string;
  impact_reference?: string;
  impact?: IImpact;
  status: "complete" | "incomplete";
}

export function useGetTravelModeOverview({
  pagination,
  filter,
  search,
  sort,
  year,
  showUnrelated = false,
  enabled = true,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  showUnrelated?: boolean;
  year?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["employees", "travel_modes", pagination, filter, sort, search, year, showUnrelated],
    enabled,
    queryFn: () =>
      axios
        .get<GetData<TravelMode>>(`/employees/travel_modes`, {
          params: {
            show_unrelated: showUnrelated,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            ...(year ? { year } : {}),
          },
        })
        .then((result) => result.data),
  });
}

export interface TravelModeOption {
  value: string;
  label: string;
  unit: string;
  impact: IImpact;
  children?: TravelModeOption[];
}

export function useGetTravelReferenceOptions() {
  return useQuery({
    queryKey: ["employees", "travel_modes", "options"],
    queryFn: () =>
      axios
        .get<TravelModeOption[]>(`/employees/travel_modes/references/options`)
        .then((result) => result.data),
  });
}

export function usePostTravelModeReference() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ travelIds, urn }: { travelIds: number[]; urn: string }) =>
      axios.post(
        "/employees/travel_modes/references",
        {},
        {
          params: {
            ids: travelIds.join(";"),
            urn: urn,
          },
        }
      ),
    onSuccess: (_) => {
      smartQueryClient.invalidateQueries(["employees"]);
    },
  });
}

export function useDeleteTravelModeReferences() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ travelModeIds }: { travelModeIds: number[] }) =>
      axios.delete("/employees/travel_modes/references", {
        params: {
          ids: travelModeIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees"]);
    },
  });
}

export function useGetTravelMode({ travelModeId }: { travelModeId: number }) {
  return useQuery({
    queryKey: ["employees", "travel_mode", travelModeId],
    queryFn: () =>
      axios
        .get<TravelMode>(`/employees/travel_modes/${travelModeId}`)
        .then((result) => result.data),
  });
}

export interface RelationsPerYear {
  year: number;
  commute_count: number;
  business_travel_count: number;
}

export function useGetTravelModeRelations({ travelModeId }: { travelModeId: number }) {
  return useQuery({
    queryKey: ["employees", "travel_mode", "relations", travelModeId],
    queryFn: () =>
      axios
        .get<RelationsPerYear[]>(`/employees/travel_modes/relations/${travelModeId}`)
        .then((result) => result.data),
  });
}

export interface TravelModePatch {
  name: string;
}

export function usePatchTravelMode() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ travelModeId, patch }: { travelModeId: number; patch: TravelModePatch }) =>
      axios.patch("/employees/travel_modes/" + travelModeId, patch),
    onSuccess: (_, { travelModeId }) => {
      smartQueryClient.invalidateQueries(["employees", "commute"]);
      smartQueryClient.invalidateQueries(["employees", "business_travel"]);
      smartQueryClient.invalidateQueries(["employees", "travel_modes"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
    },
  });
}

export function useDeleteTravelMode() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ travelModeIds }: { travelModeIds: number[] }) =>
      axios.delete("/employees/travel_modes", {
        params: {
          ids: travelModeIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["employees"]);
    },
  });
}

export function useTravelModeStatusPerYear() {
  return useQuery({
    queryKey: ["employees", "travel_modes", "years"],
    keepPreviousData: true,
    queryFn: () =>
      axios.get<YearStatus[]>("/employees/travel_modes/years", {}).then((result) => result.data),
  });
}
